import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";

import Card from "../../components/shared/Card/Card";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { HELP_CATEGORIES, HelpArticle, HelpCategory } from "./helpArticles";
import useVisibleHelpArticles from "./useVisibleHelpArticles";

function ArticleCard({ article }: { article: HelpArticle }) {
  const Icon = article.icon;

  return (
    <Link
      to={`/help/${article.slug}`}
      className="block rounded-lg bg-white shadow transition hover:ring-2 hover:ring-primary-200"
    >
      <div className="flex gap-4 p-6">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
          <Icon className="h-6 w-6" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-900">{article.title}</h3>
          <p className="mt-1 text-sm text-slate-600">{article.description}</p>
        </div>
      </div>
    </Link>
  );
}

export default function Help() {
  const { setInfo } = usePageNameContext();
  const { articles, isLoading } = useVisibleHelpArticles();
  const [search, setSearch] = useState("");

  useEffect(() => {
    setInfo({
      name: "Help",
      desc: "Guides for using Wilma day to day",
    });
  }, [setInfo]);

  const query = search.trim().toLowerCase();

  const matches = useMemo(() => {
    if (!query) return articles;
    return articles.filter(
      (article) =>
        article.title.toLowerCase().includes(query) ||
        article.description.toLowerCase().includes(query) ||
        article.category.toLowerCase().includes(query)
    );
  }, [articles, query]);

  //Only render the categories that have something in them, so a customer never
  //sees an empty section for a module they don't have.
  const grouped = useMemo(() => {
    return HELP_CATEGORIES.map((category) => ({
      category: category as HelpCategory,
      articles: matches.filter((article) => article.category === category),
    })).filter((group) => group.articles.length > 0);
  }, [matches]);

  return (
    <div className="mt-4 space-y-4">
      <Card>
        <div className="p-6">
          <p className="mt-1 text-sm text-slate-600">
            Guides for yard and office staff. If you still need a hand, raise a support ticket.
          </p>

          <div className="relative mt-4 max-w-lg">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
            </span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the guides"
              aria-label="Search the guides"
              className="block w-full rounded-md border-slate-300 pl-10 pr-10 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
              >
                <span className="sr-only">Clear search</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
      </Card>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-16">
          <LoadingWheel />
        </div>
      ) : matches.length === 0 ? (
        <Card>
          <div className="p-6 text-sm text-slate-600">
            {query
              ? `No guides match "${search.trim()}". Try a different word, or raise a support ticket.`
              : "There are no guides available for your account yet."}
          </div>
        </Card>
      ) : query ? (
        //While searching, show one flat list so results aren't buried in sections.
        <div className="grid gap-4 md:grid-cols-2">
          {matches.map((article) => (
            <ArticleCard key={article.slug} article={article} />
          ))}
        </div>
      ) : (
        grouped.map((group) => (
          <Card key={group.category} title={group.category} collapsible defaultOpen>
            <div className="grid gap-4 p-4 md:grid-cols-2">
              {group.articles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
